import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserSession } from 'hooks/useUserSession';
import { useEnvironment } from 'hooks/useEnvironment';
import { PremiumUserListResponseModel, PremiumUserModel } from 'core/domain/premiumUsers/model';
import { TABLE_PAGINATION_PAGE_DEFAULT, TABLE_PAGINATION_SIZE_DEFAULT, TablePaginationSearchValue } from 'constants/table';
import { GetPremiumUsersByIdClient } from 'core/domain/premiumUsers/repositories/getPremiumUsersByIdClient';
import { getClientTabListWithPaginationPath } from 'components/pages/App/routes/settings/config';
import { getUserPathById } from 'components/pages/App/routes/users/config';
import { GetProjectsListByClientId } from 'core/domain/project/repositories/projectsCRUD';
import { ProjectListResponseDataModel } from 'core/domain/project/model/projectModel';
import { PaginationFiltersCapabilityModel } from 'core/utils/searchFilters/models';
import { TableFilterModel, TableFilterType } from 'components/organisms/TableFilter/models';
import { checkUrlFilterKeysAreValid, checkValidUrlFilters, getFilters, parseFiltersToBeSent } from 'utils/filter';
import { TableFilterBasicModel, TableFilterBasicOptionModel } from 'utils/globalModels';
import { useMessage } from 'hooks/useMessage';
import { getDateFromString } from 'utils/date';
import { getErrorFormat } from 'core/utils/errors';
import { ClientTab } from '../../resources/models';
import { UserListColumns } from './components/UserListColumns';

export interface PremiumUserCustomModel extends PremiumUserModel {
  userActiveText: string;
  rolesLiterals: string[];
}

const projectKey = 'project_id';

export const useTabBillableUserList = () => {
  const { t } = useTranslation();
  const { token } = useUserSession();
  const { host } = useEnvironment();
  const history = useHistory();
  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const filters = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.FILTERS);
  const { clientId } = useParams<{ clientId: string }>();
  const { setMessageError } = useMessage();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataList, setDataList] = useState<PremiumUserCustomModel[]>([]);
  const [currentPage, setCurrentPage] = useState(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [pageSize, setPageSize] = useState(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [totalPages, setTotalPages] = useState<number>(0);
  const [projects, setProjects] = useState<ProjectListResponseDataModel[]>([]);
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');
  const [currentFilters, setCurrentFilters] = useState<TableFilterModel[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);

  const userActiveDescription = t('_BILLABLE_USER_LIST_USER_ACTIVE');
  const userNotActiveDescription = t('_BILLABLE_USER_LIST_USER_NOT_ACTIVE');

  const columnLiterals: Record<string, string> = {
    nameColumnTitle: t('name'),
    emailColumnTitle: t('email'),
    rolesColumnTitle: t('roles'),
    projectColumnTitle: t('projects'),
    userActiveColumnTitle: t('_BILLABLE_USER_LIST_COLUMNS_ACTIVE_USER_TITLE'),
    userActiveTooltipText: t('_BILLABLE_USER_LIST_COLUMNS_ACTIVE_USER_TOOLTIP_TITLE'),
    lastAccessColumnTitle: t('_BILLABLE_USER_LIST_COLUMNS_LAST_ACCESS_TITLE'),
    confirmedAtColumnTitle: t('_BILLABLE_USER_LIST_COLUMNS_CONFIRMED_AT_TITLE'),
    notReportedText: t('not_reported'),
  };

  const columns = UserListColumns({ columnLiterals });

  const getUsers = async (userData: { current: number; size: number; filters: string }): Promise<PremiumUserListResponseModel> => {
    const { current, size, filters } = userData;
    try {
      const parsedFilters = parseFiltersToBeSent(filters);
      const response = await GetPremiumUsersByIdClient({
        host,
        token,
        clientId,
        page: current,
        size,
        filters: parsedFilters,
      });

      return response;
    } catch (error) {
      throw getErrorFormat(error);
    }
  };

  const getClientProjectsList = async (clientId: string) => {
    setIsLoadingFilters(true);
    try {
      const projectsResponse = await GetProjectsListByClientId(clientId);
      setProjects(projectsResponse.data);
    } catch (error) {
      setMessageError({ description: t('_BILLABLE_USER_LIST_ERROR_PROJECTS_MESSAGE') });
      setIsLoadingFilters(false);
    }
  };

  const getData = async (params: { current: number; size: number; filters: string }) => {
    setIsLoading(true);
    try {
      const usersListResponse = await getUsers(params);

      if (!!usersListResponse.filters) {
        const isUrlFiltersValid = checkUrlFilterKeysAreValid({
          urlFilter: params.filters,
          dataFilters: usersListResponse.filters.capability,
        });
        isUrlFiltersValid
          ? setStateValues(usersListResponse)
          : resetUrl({ urlFilter: params.filters, dataFilters: usersListResponse.filters.capability });
      }
    } catch (error) {
      setMessageError({ description: t('_BILLABLE_USER_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoading(false);
    }
  };

  const parseUserListToCustomUserList = (userList: PremiumUserModel[]): PremiumUserCustomModel[] => {
    return userList.map((user) => {
      const userActiveText = user.userActive ? userActiveDescription : userNotActiveDescription;
      const rolesLiterals = user.userRoles.map((role) => t(`ROLES.${role}`));
      const confirmationDate = getDateFromString(user.userConfirmedAt);
      const lastAccessDate = user.userLastAccess ? getDateFromString(user.userLastAccess) : null;

      return {
        ...user,
        userActiveText,
        rolesLiterals,
        userConfirmedAt: confirmationDate,
        userLastAccess: lastAccessDate,
      };
    });
  };

  const setStateValues = (response: PremiumUserListResponseModel) => {
    const { data, meta } = response;
    const parsedUserList = parseUserListToCustomUserList(data.userList);
    setDataList(parsedUserList);
    !!meta && setTotalPages(meta.totalPages);
  };

  const resetUrl = ({ urlFilter, dataFilters }: { urlFilter: string; dataFilters: PaginationFiltersCapabilityModel }) => {
    const validUrlFilterKeys = checkValidUrlFilters({ urlFilter, dataFilters });
    const newUrlFilter = !!validUrlFilterKeys.length ? validUrlFilterKeys.join(',') : '';
    onFilter(newUrlFilter);
  };

  const setInitialFilters = (projects: ProjectListResponseDataModel[]) => {
    const items: TableFilterBasicModel[] = [{ key: projectKey, label: t('projects'), type: TableFilterType.CHECKBOX }];
    const options: TableFilterBasicOptionModel[] = projects.map(({ id, name }) => ({ key: projectKey, value: id, label: name }));
    const initialFilters = getFilters({ items, options, currentFilters: currentFilterValue });
    setCurrentFilters(initialFilters);
    setIsLoadingFilters(false);
  };

  const onRowClick = (user: PremiumUserCustomModel) => {
    history.push(getUserPathById(user.id));
  };

  const onFilter = (url: string) => {
    history.push(
      getClientTabListWithPaginationPath({
        clientId,
        tab: ClientTab.BILLABLE_USERS,
        page: String(currentPage),
        size: String(pageSize),
        filters: url,
      })
    );
  };

  const onChangePage = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    setPageSize(pageSize!);
    history.push(
      getClientTabListWithPaginationPath({
        clientId,
        page: String(page),
        size: String(pageSize!),
        tab: ClientTab.BILLABLE_USERS,
        filters: currentFilterValue,
      })
    );
  };

  useEffect(() => {
    const newPage = Number(page || TABLE_PAGINATION_PAGE_DEFAULT);
    const newSize = Number(size || TABLE_PAGINATION_SIZE_DEFAULT);
    const newFilters = filters || '';

    getData({ current: newPage, size: newSize, filters: newFilters });
    setCurrentPage(newPage);
    setPageSize(newSize);
    setCurrentFilterValue(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, filters]);

  useEffect(() => {
    !!clientId && getClientProjectsList(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    !!projects?.length && setInitialFilters(projects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, currentFilterValue]);

  return {
    columns,
    dataList,
    totalPages,
    pageSize,
    currentPage,
    filters: currentFilters,
    onChangePage,
    onRowClick,
    onFilter,
    isLoading,
    isLoadingFilters,
  };
};
